import { useState, useEffect } from "react";
import { useAuth } from '../AuthContext';
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Truck, BarChart3, ArrowLeft, Clock, CheckCircle2, AlertCircle, PlusCircle, ShieldCheck, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import PERMISSIONS from '../../Permissions';
import Util from '../../Util';
import http from "../../Api/http";

const SpareParts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    if (user?.ID) {
      loadShipments();
    }
  }, [user]);

  const loadShipments = async () => {
    try {
      setIsLoading(true);
      let obj = {
        SupplierId: user?.Role === 3 ? user.ID : 0,
        StatusId: 0
      }
      const res = await http.postJsonAxios(http.actions.Shipments.GetShipmentsReport, obj, t);
      if (res.status === 200 && res.data.success) {
        setData(res.data.obj);
      }
    } catch { }
    finally {
      setIsLoading(false);
    }
  };

  // ✅ بدل Redirect
  if (!user) {
    navigate("/");
    return null;
  }

  const shipments = user?.Role === 3 ? data : data;

  // ✅ حذف TypeScript
  const countByStatus = (statuses) =>
    shipments.filter((s) => statuses.includes(s.StatusId)).length;

  const newCount = countByStatus([1]);
  const sentCount = countByStatus([2]);
  const receivedCount = countByStatus([3]);
  const returnCount = countByStatus([4, 5]);
  const pendingReceive = countByStatus([2]);
  const approved = countByStatus([6]);

  const cardVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.4 },
    }),
  };
  const greetingHour = new Date().getHours();
  const greeting =
    greetingHour < 12 ? t('goodMorning') : greetingHour < 17 ? t('goodEvening') : t('goodNight');

  const StatPill = ({ label, value, color }) => {
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs ${color}`}>
        {value} {label}
      </span>
    );
  }

  const SectionCard = ({
    index,
    icon,
    gradient,
    iconBg,
    title,
    description,
    stats,
    href,
    actionLabel,
    accentColor,
  }) => {
    return (
      <motion.div
        custom={index}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        className="group relative bg-card border border-border/50 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col"
        dir={t('dir')}
      >
        {/* Top gradient bar */}
        <div className={`h-1.5 w-full ${gradient}`} />

        <div className="p-6 flex flex-col flex-1">
          {/* Icon */}
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 shadow-sm ${iconBg}`}>
            {icon}
          </div>

          {/* Title & Description */}
          <h3 className="text-lg font-bold text-foreground mb-1.5">{title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1">{description}</p>

          {/* Stats */}
          {stats && <div className="flex flex-wrap gap-2 mb-5">{stats}</div>}

          {/* CTA */}
          <button
            className={`
                w-full flex items-center justify-center gap-2 px-6 h-10 rounded gap-2 transition-all duration-200 group-hover:gap-3
                ${accentColor}
              `}
            onClick={() => navigate(href)}
          >
            <ArrowLeft className="w-4 h-4 rotate-180" />
            {actionLabel}
          </button>
        </div>
      </motion.div>
    );
  }

  const cardsList = [
    Util.hasPermission(user, PERMISSIONS.ADD_Order) && {
      icon: <Package className="w-7 h-7 text-green-600" />,
      gradient: "bg-gradient-to-r from-green-500 to-green-400",
      iconBg: "bg-blue-50",
      title: t('myOrders'),
      description: t('ordersHint'),
      stats: (
        <>
          {newCount > 0 && <StatPill label={t('new')} value={newCount} color="bg-blue-100 text-blue-700" />}
          {sentCount > 0 && <StatPill label={t('sent')} value={sentCount} color="bg-amber-100 text-amber-700" />}
          {receivedCount > 0 && <StatPill label={t('received')} value={receivedCount} color="bg-emerald-100 text-emerald-700" />}
          {returnCount > 0 && <StatPill label={t('returnedToSupplier')} value={returnCount} color="bg-red-100 text-red-700" />}
          {approved > 0 && <StatPill label={t('approved')} value={approved} color="bg-slate-100 text-slate-700" />}
          {newCount + sentCount + receivedCount + returnCount + approved === 0 && (<span className="text-xs text-muted-foreground">{t('noOrders')}</span>)}
        </>
      ),
      href: "/SupplierDashboard",
      actionLabel: t('myOrders'),
      accentColor: "rounded bg-green-600 hover:bg-green-700 text-white",
    },
    Util.hasPermission(user, PERMISSIONS.Receiving_Orders) && {
      icon: <Truck className="w-7 h-7 text-orange-600" />,
      gradient: "bg-gradient-to-r from-orange-500 to-orange-400",
      iconBg: "bg-orange-50",
      title: t('receivingShipments'),
      description: t('receivingHint'),
      stats: (
        <>
          {pendingReceive > 0 ? (
            <StatPill label={t('waitReceive')} value={pendingReceive} color="bg-amber-100 text-amber-700" />
          ) : (
            <span className="text-xs text-muted-foreground">{t('noOrders')}</span>
          )}
          {receivedCount > 0 && (
            <StatPill label={t('received')} value={receivedCount} color="bg-emerald-100 text-emerald-700" />
          )}
        </>
      ),
      href: "/EmployeeDashboard",
      actionLabel: t('viewOrders'),
      accentColor: "bg-orange-600 hover:bg-orange-700 text-white",
    },
    Util.hasPermission(user, PERMISSIONS.Approve_Order) && {
      icon: <ShieldCheck className="w-7 h-7 text-purple-600" />,
      gradient: "bg-gradient-to-r from-purple-500 to-purple-400",
      iconBg: "bg-purple-50",
      title: t('monitorShipments'),
      description: t('monitorHint'),
      stats: (
        <>
          {newCount > 0 && <StatPill label={t('new')} value={newCount} color="bg-slate-100 text-slate-600" />}
          {sentCount > 0 && <StatPill label={t('sent')} value={sentCount} color="bg-amber-100 text-amber-700" />}
          {receivedCount > 0 && <StatPill label={t('received')} value={receivedCount} color="bg-emerald-100 text-emerald-700" />}
          {returnCount > 0 && <StatPill label={t('returnedToSupplier')} value={returnCount} color="bg-red-100 text-red-700" />}
          {returnCount > 0 && <StatPill label={t('returnedToSupplier')} value={returnCount} color="bg-red-100 text-red-700" />}
          {approved > 0 && <StatPill label={t('approved')} value={approved} color="bg-slate-100 text-slate-700" />}
        </>
      ),
      href: "/ManagerDashboard",
      actionLabel: t('monitorShipments'),
      accentColor: "bg-purple-600 hover:bg-purple-700 text-white",
    },
    Util.hasPermission(user, PERMISSIONS.Orders_Report) && {
      icon: <BarChart3 className="w-7 h-7 text-blue-600" />,
      gradient: "bg-gradient-to-r from-blue-500 to-blue-400",
      iconBg: "bg-blue-50",
      title: t('shipmentsReport'),
      description: t('orderReportHint'),
      href: "/ShipmentsReport",
      actionLabel: t('viewReport'),
      accentColor: "bg-blue-600 hover:bg-blue-700 text-white",
    },
  ].filter(Boolean);

  const cards = cardsList;

  /* ── Quick stat row (only for supplier & manager) ── */
  const showQuickStats = user.Role !== 4;

  const quickStats = [
    { label: t('new'), value: newCount, icon: PlusCircle, color: "text-slate-600", bg: "bg-slate-100" },
    { label: t('sent'), value: sentCount, icon: Truck, color: "text-amber-600", bg: "bg-amber-100" },
    { label: t('received'), value: receivedCount, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-100" },
    { label: t('returnedToSupplier'), value: returnCount, icon: RotateCcw, color: "text-red-500", bg: "bg-red-100" },
  ];

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto" dir={t('dir')}>
      {/* Welcome Banner */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="relative mb-8 rounded-2xl overflow-hidden bg-gradient-to-br from-primary via-primary/90 to-indigo-700 text-primary-foreground p-7 shadow-xl"
      >
        {/* Decorative blobs */}
        <div className="absolute -top-10 -left-10 w-48 h-48 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-12 left-20 w-64 h-64 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute top-4 left-6 w-20 h-20 bg-white/5 rounded-full pointer-events-none" />

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-4">
          {/* <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-inner shrink-0">
            <span className="text-3xl font-black">{user?.Username?.charAt(0)}</span>
          </div> */}
          <div>
            {/* <p className="text-primary-foreground/70 text-sm font-medium mb-0.5">{greeting}!</p> */}
            <h1 className="text-2xl font-extrabold tracking-tight">{t('myOrders')}</h1>
            {/*<span className="inline-block mt-1.5 bg-white/20 text-xs px-3 py-1 rounded-full">
              {user?.RoleName}
            </span> */}
          </div>
          <div className="sm:ms-auto text-sm text-primary-foreground/70 flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            {greeting}
          </div>
        </div>
      </motion.div>

      {/* Quick Stats Row */}
      {/* {showQuickStats && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8"
        >
          {quickStats.map((stat) => (
            <div
              key={stat.label}
              className="bg-card border border-border/50 rounded-xl p-4 flex items-center gap-3 shadow-sm hover:shadow transition-shadow"
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${stat.bg}`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <div>
                <p className="text-xl font-black text-foreground leading-none mb-0.5">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          ))}
        </motion.div>
      )} */}

      {/* Section heading */}
      {/* <motion.h2
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.25 }}
        className="text-sm text-muted-foreground uppercase tracking-wider mb-4"
      >
        {t('availableSections')}
      </motion.h2> */}

      {/* Cards Grid */}
      <div
        className={`grid gap-5 ${cards.length === 1
          ? "grid-cols-1 max-w-sm"
          : cards.length === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          }`}
      >
        {cards.map((card, i) => (
          <SectionCard key={card.href + i} index={i} {...card} />
        ))}
      </div>

      {/* Employee: no stats row, show a quick info note */}
      {user.role === "employee" && pendingReceive > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3"
        >
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-amber-800">
              لديك {pendingReceive} شحنة بانتظار الاستلام
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              تأكد من استلام الشحنات المرسلة إليك في أقرب وقت ممكن.
            </p>
          </div>
        </motion.div>
      )}

      {/* Employee: received stat */}
      {user.role === "employee" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45 }}
          className="mt-4 grid grid-cols-2 gap-3 max-w-sm"
        >
          <div className="bg-card border border-border/50 rounded-xl p-4 flex items-center gap-3 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <Truck className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xl font-black leading-none mb-0.5">{pendingReceive}</p>
              <p className="text-xs text-muted-foreground">بانتظار الاستلام</p>
            </div>
          </div>
          <div className="bg-card border border-border/50 rounded-xl p-4 flex items-center gap-3 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xl font-black leading-none mb-0.5">{receivedCount}</p>
              <p className="text-xs text-muted-foreground">تم استلامها</p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default SpareParts;